import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { pollsAPI } from '../../services/api'
import { useAuthStore } from '../../store/authStore'
import { apiErrorMessage } from '../../utils/apiError'
import type { Poll } from '../../types/polls'
import PollCard from './PollCard'

/** Why the viewer can't vote on `poll`, or null when there's nothing to say. */
const noteFor = (poll: Poll, signedIn: boolean, isCreator: boolean): string | null => {
  if (isCreator || poll.viewer_vote !== null) return null
  if (!signedIn) return 'Log in to vote.'
  if (poll.subscriber_only && poll.viewer_can_vote === false) return 'Subscribers can vote on this poll.'
  return null
}

/**
 * A creator's open polls, on their profile. Anyone sees the counts; a
 * viewer who may vote picks an option there.
 */
const CreatorPolls = ({ creatorId }: { creatorId: number }) => {
  const user = useAuthStore((state) => state.user)
  const [polls, setPolls] = useState<Poll[]>([])
  const [voting, setVoting] = useState<number | null>(null)

  useEffect(() => {
    let live = true
    pollsAPI
      .list(creatorId)
      .then((list) => live && setPolls(list))
      .catch(() => live && setPolls([]))
    return () => {
      live = false
    }
  }, [creatorId, user?.id])

  const vote = async (poll: Poll, optionIndex: number) => {
    setVoting(poll.id)
    try {
      const updated = await pollsAPI.vote(poll.id, optionIndex)
      setPolls((prev) => prev.map((p) => (p.id === updated.id ? updated : p)))
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't record your vote"))
    } finally {
      setVoting(null)
    }
  }

  // A profile with no open polls shows nothing.
  if (polls.length === 0) return null

  return (
    <section aria-labelledby="polls-heading" className="space-y-3">
      <h2 id="polls-heading" className="text-lg font-semibold text-gray-900">
        Polls
      </h2>
      {polls.map((poll) => (
        <PollCard
          key={poll.id}
          poll={poll}
          onVote={(i) => vote(poll, i)}
          voting={voting === poll.id}
          note={noteFor(poll, !!user, user?.id === creatorId)}
        />
      ))}
    </section>
  )
}

export default CreatorPolls
